import React, { useState } from 'react';

const VerificationDashboard = ({ extractedData, onVerify }) => {
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedGstin, setVerifiedGstin] = useState('');

  const handleVerify = async () => {
    const currentGstin = extractedData?.gstin;
    if (isVerifying || !currentGstin || verifiedGstin === currentGstin) return;
    
    setIsVerifying(true);
    try {
      const result = await onVerify(extractedData);
      setVerificationResult(result);
      setVerifiedGstin(currentGstin);
    } catch (error) {
      setVerificationResult({
        status: 'error',
        message: 'Verification failed'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'match':
        return <span className="text-green-500">✅</span>;
      case 'partial':
        return <span className="text-yellow-500">⚠️</span>;
      case 'mismatch':
        return <span className="text-red-500">❌</span>;
      default:
        return <span className="text-gray-500">-</span>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'match':
        return 'bg-green-50 border-green-200';
      case 'partial':
        return 'bg-yellow-50 border-yellow-200';
      case 'mismatch':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (!extractedData?.gstin) {
    return null;
  }

  // Reset verification if GSTIN changes
  if (extractedData.gstin !== verifiedGstin && verificationResult) {
    setVerificationResult(null);
    setVerifiedGstin('');
  }

  const isAlreadyVerified = verifiedGstin === extractedData.gstin && verificationResult;

  return (
    <div className="mt-6 p-4 border rounded-lg bg-blue-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-blue-800">GST Verification</h3>
        <button
          onClick={handleVerify}
          disabled={isVerifying || isAlreadyVerified}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isVerifying ? 'Verifying...' : isAlreadyVerified ? 'Verified' : 'Verify with Official API'}
        </button>
      </div>

      {verificationResult && (
        <div className="space-y-4">
          {verificationResult.status === 'error' ? (
            <div className="p-3 bg-red-100 border border-red-300 rounded text-red-700">
              {verificationResult.message}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(verificationResult.verification_results || {}).map(([field, result]) => (
                  <div key={field} className={`p-3 border rounded ${getStatusColor(result.status)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{field.replace('_', ' ')}</span>
                      {getStatusIcon(result.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Extracted:</span>
                        <div className="font-mono">{result.extracted || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Official API:</span>
                        <div className="font-mono">{result.api || 'N/A'}</div>
                      </div>
                    </div>
                    {result.similarity && (
                      <div className="mt-1 text-xs text-gray-600">
                        Similarity: {(result.similarity * 100).toFixed(1)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-gray-100 border rounded">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Overall Verification Score</span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    verificationResult.overall_match?.status === 'verified' 
                      ? 'bg-green-100 text-green-800'
                      : verificationResult.overall_match?.status === 'partial'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {verificationResult.overall_match?.score}% 
                    ({verificationResult.overall_match?.status})
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {verificationResult.overall_match?.matched_fields} of {verificationResult.overall_match?.total_fields} fields matched
                </div>
              </div>

              {verificationResult.api_data && (
                <div className="mt-4 bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Additional Details from API</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {verificationResult.api_data.client_id && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Client ID:</span>
                        <div className="font-mono text-xs text-gray-500 mt-1">{verificationResult.api_data.client_id}</div>
                      </div>
                    )}
                    {verificationResult.api_data.taxpayer_type && (
                      <div className="text-xs">
                        <span className="text-gray-600">Taxpayer Type:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.taxpayer_type}</div>
                      </div>
                    )}
                    {verificationResult.api_data.constitution_of_business && (
                      <div className="text-xs">
                        <span className="text-gray-600">Constitution of Business:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.constitution_of_business}</div>
                      </div>
                    )}
                    {verificationResult.api_data.center_jurisdiction && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Center Jurisdiction:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.center_jurisdiction}</div>
                      </div>
                    )}
                    {verificationResult.api_data.state_jurisdiction && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">State Jurisdiction:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.state_jurisdiction}</div>
                      </div>
                    )}
                    {verificationResult.api_data.aadhaar_validation && (
                      <div className="text-xs">
                        <span className="text-gray-600">Aadhaar Validation:</span>
                        <div className="font-medium text-blue-900 mt-1">
                          {verificationResult.api_data.aadhaar_validation}
                          {verificationResult.api_data.aadhaar_validation_date && 
                            verificationResult.api_data.aadhaar_validation_date !== '1800-01-01' &&
                            ` (${verificationResult.api_data.aadhaar_validation_date})`}
                        </div>
                      </div>
                    )}
                    {verificationResult.api_data.field_visit_conducted && (
                      <div className="text-xs">
                        <span className="text-gray-600">Field Visit Conducted:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.field_visit_conducted}</div>
                      </div>
                    )}
                    {verificationResult.api_data.date_of_cancellation && 
                     verificationResult.api_data.date_of_cancellation !== '1800-01-01' && (
                      <div className="text-xs">
                        <span className="text-gray-600">Date of Cancellation:</span>
                        <div className="font-medium text-red-600 mt-1">{verificationResult.api_data.date_of_cancellation}</div>
                      </div>
                    )}
                    {verificationResult.api_data.nature_bus_activities && 
                     verificationResult.api_data.nature_bus_activities.length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Nature of Business Activities:</span>
                        <div className="font-medium text-blue-900 mt-1">
                          {verificationResult.api_data.nature_bus_activities.join(', ')}
                        </div>
                      </div>
                    )}
                    {verificationResult.api_data.nature_of_core_business_activity_code && 
                     verificationResult.api_data.nature_of_core_business_activity_code !== 'NA' && (
                      <div className="text-xs">
                        <span className="text-gray-600">Core Business Activity Code:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.nature_of_core_business_activity_code}</div>
                      </div>
                    )}
                    {verificationResult.api_data.nature_of_core_business_activity_description && 
                     verificationResult.api_data.nature_of_core_business_activity_description !== 'NA' && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Core Business Activity:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.nature_of_core_business_activity_description}</div>
                      </div>
                    )}
                    {verificationResult.api_data.filing_status && 
                     verificationResult.api_data.filing_status.length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Filing Status:</span>
                        <div className="font-medium text-blue-900 mt-1">
                          {JSON.stringify(verificationResult.api_data.filing_status)}
                        </div>
                      </div>
                    )}
                    {verificationResult.api_data.filing_frequency && 
                     verificationResult.api_data.filing_frequency.length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Filing Frequency:</span>
                        <div className="font-medium text-blue-900 mt-1">
                          {JSON.stringify(verificationResult.api_data.filing_frequency)}
                        </div>
                      </div>
                    )}
                    {verificationResult.api_data.address_details && 
                     Object.keys(verificationResult.api_data.address_details).length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Address Details:</span>
                        <div className="font-medium text-blue-900 mt-1">
                          {JSON.stringify(verificationResult.api_data.address_details)}
                        </div>
                      </div>
                    )}
                    {verificationResult.api_data.hsn_info && 
                     Object.keys(verificationResult.api_data.hsn_info).length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">HSN Info:</span>
                        <div className="font-medium text-blue-900 mt-1">
                          {JSON.stringify(verificationResult.api_data.hsn_info)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VerificationDashboard;